import { reflect } from '@effector/reflect';
import { TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Edit } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
	CASHDESK_NAME_MAX_LENGTH,
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
} from 'entities/cashdesk';
import { viewerModel } from 'entities/viewer';
import { translated, $t } from 'shared/lib';
import { createModalForm, ModalFormProps, MultiSelect } from 'shared/ui';
import { EditCashdeskModel } from '../../model';

export const createEditCashdeskModal = (model: EditCashdeskModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-cashdesk-name-input',
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
			label: translated('edit-cashdesk.name'),
			placeholder: translated('edit-cashdesk.name'),
			errorProps: { 'data-test-id': 'edit-cashdesk-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-cashdesk.name'),
						maxLength: CASHDESK_NAME_MAX_LENGTH,
						minLength: CASHDESK_NAME_MIN_LENGTH,
					}),
			),
			required: true,
		},
	});

	const CashiersField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'edit-cashdesk-cashiers-select',
			value: model.form.fields.cashierIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => model.form.fields.cashierIds.change(value.map(Number)),
			data: model.$cashiers,
			disabled: model.form.fields.cashierIds.$disabled,
			label: translated('edit-cashdesk.cashiers'),
			placeholder: translated('edit-cashdesk.cashiers'),
			nothingFoundMessage: translated('edit-cashdesk.cashiers-not-found'),
			clearable: true,
			fullWidth: true,
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-cashdesk-note-input',
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
			label: translated('edit-cashdesk.note'),
			placeholder: translated('edit-cashdesk.note'),
			errorProps: { 'data-test-id': 'edit-cashdesk-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-cashdesk.note'),
						maxLength: CASHDESK_NOTE_MAX_LENGTH,
					}),
			),
		},
	});

	const EditCashdeskModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, pristine] = useUnit([model.$pending, model.form.$pristine]);

		return (
			<ModalForm
				ref={ref}
				title={t('edit-cashdesk.title')}
				cancelButtonProps={{
					children: t('edit-cashdesk.cancel'),
				}}
				submitButtonProps={{
					children: t('edit-cashdesk.save'),
					loading: pending,
					disabled: pristine,
				}}
				{...props}
			>
				<NameField />
				<CashiersField />
				<NoteField />
			</ModalForm>
		);
	});

	const EditCashdeskModalActionIcon = reflect({
		view: ModalForm.ActionIcon,
		bind: {
			'data-test-id': 'edit-cashdesk-action-icon',
			variant: 'ghost-secondary',
			disabled: not(viewerModel.$canWriteCashdesks),
			children: <Edit />,
		},
	});

	return Object.assign(EditCashdeskModal, {
		ActionIcon: EditCashdeskModalActionIcon,
	});
};
